import type { RolePermission, PermissionCheck } from '../types';

/**
 * Permission system for role-based access control (RBAC)
 * 
 * Design principles:
 * 1. Allowlist by default: resources require explicit permission to access
 * 2. Blocklist overrides: if any role blocks access, user is denied
 * 3. Parent-child inheritance: parent permissions cascade to children
 * 4. Multiple roles: user can have multiple roles, permissions are merged
 * 5. Read-write dependency: write requires read (can't write what you can't see)
 */

/**
 * Resource hierarchy map
 * Defines parent-child relationships for permission inheritance
 * Exported so UI components can receive and display resource trees
 * 
 * ⚠️ CRITICAL: When adding new features/routes to the application:
 * 1. Add the resource path here (e.g., 'feature/subfeature': 'feature')
 * 2. Add navigation entry to src/components/Sidebar.tsx
 * 3. Add route definition to src/App.tsx
 * 4. Update db/schema.sql role_permissions to grant access
 * 
 * If a resource is NOT in this hierarchy, it will NOT appear in the
 * Roles & Permissions management UI!
 */
export const RESOURCE_HIERARCHY: Record<string, string | null> = {
  // Top-level resources (no parent)
  'dashboard': null,
  'finance': null,
  'oms': null,
  'admin': null,
  'workshop': null,
  'inventory': null,
  'etsy': null,
  
  // Dashboard children
  'dashboard/overview': 'dashboard',
  'dashboard/financial': 'dashboard',
  'dashboard/kanban': 'dashboard',
  
  // Finance children
  'finance/accounts': 'finance',
  'finance/coa': 'finance',
  'finance/journal': 'finance',
  'finance/transactions': 'finance',
  'finance/reports': 'finance',
  'finance/periods': 'finance',
  'finance/reconciliation': 'finance',
  'finance/import': 'finance',
  'finance/enhanced': 'finance',

  // Enhanced Finance children
  'finance/enhanced/dashboard': 'finance/enhanced',
  'finance/enhanced/entries': 'finance/enhanced',
  'finance/enhanced/entries/journal': 'finance/enhanced/entries',
  'finance/enhanced/entries/posted': 'finance/enhanced/entries',
  'finance/enhanced/entries/templates': 'finance/enhanced/entries',
  'finance/enhanced/templates': 'finance/enhanced',
  'finance/enhanced/accounts': 'finance/enhanced',
  'finance/enhanced/accounts/coa': 'finance/enhanced/accounts',
  'finance/enhanced/accounts/reconciliation': 'finance/enhanced/accounts',
  'finance/enhanced/coa': 'finance/enhanced',
  'finance/enhanced/reconciliation': 'finance/enhanced',
  'finance/enhanced/periods': 'finance/enhanced',
  'finance/enhanced/reports': 'finance/enhanced',
  'finance/enhanced/operations': 'finance/enhanced',
  
  // OMS children (Order Management System - consolidated orders, tracking, kanban)
  'oms/orders': 'oms',
  'oms/customers': 'oms',
  'oms/products': 'oms',
  'oms/coupons': 'oms',
  'oms/sales-config': 'oms',
  'oms/time-tracking': 'oms',
  'oms/mileage': 'oms',
  'oms/kanban': 'oms',
  
  // Inventory children
  'inventory/filament': 'inventory',
  
  // Etsy children
  'etsy/integration': 'etsy',
  'etsy/import': 'etsy',
  
  // Admin children
  'admin/audit': 'admin',
  'admin/profile': 'admin',
  'admin/roles': 'admin',
  'admin/settings': 'admin',
  'admin/users': 'admin',
  'admin/users/list': 'admin/users',
  'admin/users/manage': 'admin/users',
  'admin/users/merge': 'admin/users',
  
  // Workshop children
  'workshop/printers': 'workshop',
  'workshop/print-queue': 'workshop',
  'workshop/spools': 'workshop',
  'workshop/maintenance': 'workshop',
  'workshop/inventory': 'workshop',
};

/**
 * Get parent resource path for inheritance
 */
function getParentResource(resourcePath: string): string | null {
  return RESOURCE_HIERARCHY[resourcePath] ?? null;
}

/**
 * Merge multiple permission checks using RBAC rules:
 * - If any role blocklists, access is denied (blocklist wins)
 * - Otherwise, grant access if any role allowlists
 * - Write permission requires read permission
 */
export function mergePermissions(permissions: Array<{ perm: RolePermission; inherited: boolean }>): PermissionCheck {
  let hasReadAllowlist = false;
  let hasWriteAllowlist = false;
  let hasReadBlocklist = false;
  let hasWriteBlocklist = false;
  
  // First pass: check for blocklists (they override everything)
  // BLOCKLIST SEMANTICS: For blocklists, canRead=false means "block read access"
  // We set hasReadBlocklist=true when isBlocklist=true and canRead=false
  // This denies the permission (canRead: false = "deny read")
  for (const { perm } of permissions) {
    if (perm.isBlocklist) {
      if (!perm.canRead) {hasReadBlocklist = true;}
      if (!perm.canWrite) {hasWriteBlocklist = true;}
    }
  }
  
  // Second pass: check for allowlists (only if not blocked)
  for (const { perm } of permissions) {
    if (!perm.isBlocklist) {
      if (perm.canRead) {hasReadAllowlist = true;}
      if (perm.canWrite) {hasWriteAllowlist = true;}
    }
  }
  
  // Apply blocklist override rules
  const canRead = !hasReadBlocklist && hasReadAllowlist;
  const canWrite = !hasWriteBlocklist && hasWriteAllowlist;
  
  // Write requires read (can't write what you can't see)
  const finalCanWrite = canWrite && canRead;
  
  return {
    canRead,
    canWrite: finalCanWrite,
    reason: hasReadBlocklist ? 'Blocked by role' : !hasReadAllowlist ? 'No permission granted' : undefined
  };
}

/**
 * Evaluate permissions for a specific resource path
 * Considers:
 * - Direct permissions on the resource
 * - Inherited permissions from parent resources
 * - Multiple roles per user
 * - Allowlist/blocklist rules
 * 
 * @param userPermissions - All permissions from all user's roles
 * @param resourcePath - Resource to check (e.g., "finance/journal")
 * @returns PermissionCheck with canRead and canWrite flags
 */
export function evaluatePermissions(
  userPermissions: RolePermission[],
  resourcePath: string
): PermissionCheck {
  // Collect all relevant permissions (direct + inherited from parents)
  const relevantPermissions: Array<{ perm: RolePermission; inherited: boolean }> = [];
  
  // 1. Check for direct permissions on this exact resource
  for (const perm of userPermissions) {
    if (perm.resourcePath === resourcePath) {
      relevantPermissions.push({ perm, inherited: false });
    }
  }
  
  // 2. Check for inherited permissions from parent resources
  let currentResource: string | null = resourcePath;
  while (currentResource !== null) {
    const parent = getParentResource(currentResource);
    if (parent !== null) {
      for (const perm of userPermissions) {
        if (perm.resourcePath === parent) {
          relevantPermissions.push({ perm, inherited: true });
        }
      }
    }
    currentResource = parent;
  }
  
  // 3. If no permissions found at all, deny by default (allowlist principle)
  if (relevantPermissions.length === 0) {
    return {
      canRead: false,
      canWrite: false,
      reason: 'No permission granted'
    };
  }
  
  // 4. Merge all permissions following RBAC rules
  return mergePermissions(relevantPermissions);
}

/**
 * Check if user has permission to access a resource
 * Convenience function for quick permission checks
 */
export function checkPermission(
  userPermissions: RolePermission[],
  resourcePath: string,
  action: 'read' | 'write'
): boolean {
  const check = evaluatePermissions(userPermissions, resourcePath);
  return action === 'read' ? check.canRead : check.canWrite;
}

/**
 * Get all child resources of a parent resource
 * Used for bulk permission checking (e.g., checking if user can see any child tabs)
 */
export function getChildResources(parentPath: string): string[] {
  return Object.keys(RESOURCE_HIERARCHY).filter(
    path => RESOURCE_HIERARCHY[path] === parentPath
  );
}

/**
 * Check if user has permission to access ANY child of a parent resource
 * Useful for determining if parent tab should be visible
 */
export function hasAccessToAnyChild(
  userPermissions: RolePermission[],
  parentPath: string,
  action: 'read' | 'write'
): boolean {
  const children = getChildResources(parentPath);
  return children.some(child => checkPermission(userPermissions, child, action));
}

/**
 * Alias for checkPermission - used by Sidebar component
 * Checks if a specific permission string exists in the user's permissions
 */
export function hasPermission(
  userPermissions: RolePermission[],
  permissionString: string
): boolean {
  // Parse permission string format "parent.child.action"
  // e.g., "finance.journal.read" -> resource: "finance/journal", action: "read"
  const parts = permissionString.split('.');
  if (parts.length < 2) {return false;}
  
  const action = parts[parts.length - 1] as 'read' | 'write';
  const resourceParts = parts.slice(0, -1);
  const resourcePath = resourceParts.join('/');
  
  return checkPermission(userPermissions, resourcePath, action);
}
